// src/components/EmptyState.jsx
//
// Replaces bare "No X yet" text across the doctor, subscriber and patient
// portals. Renders via the shared .empty-state/.empty-icon/.empty-title/
// .empty-desc classes (index.css) so it matches the Navigator portal's own
// empty states (e.g. PatientListPage, DashboardPage) instead of a one-off
// inline style.
//
// WHY THIS GREW
// -------------
// On a patient's first login EVERY page is empty at once, so these are not
// fallbacks — they are the product. Two things were wrong with treating them
// as fallbacks:
//
//   1. Pages that used this component still looked unfinished. There was no
//      container, so the state floated on the cream canvas looking like a page
//      that had failed to load, and the title rendered in --text-secondary,
//      which reads as disabled next to a real heading.
//   2. Pages that did NOT use it hand-rolled their own. My Care Plan built a
//      bordered card with a 40px emoji and its own type scale; My Lab Tests and
//      My Appointments emitted one line of grey text with an inline "→" link
//      and no icon at all. Three designs of "nothing here yet" in one sidebar.
//
// So: `framed` for the surface, `icon` accepting a name from the shared SVG set
// (Icon) instead of an emoji, `secondaryAction` for the "or do this
// instead" route, and `hint` for the reassurance line under the CTA.
//
// Props:
//   icon            — a name from ICON_NAMES (rendered as an SVG badge),
//                     or any other string, which is rendered as an emoji so
//                     existing call sites keep exactly the look they have
//   title           — what's missing
//   message         — why it's empty + what to do
//   action          — optional { label, onClick } primary CTA
//   secondaryAction — optional { label, onClick } rendered as a text link
//   hint            — optional reassurance line under the actions
//   framed          — draw the surface/border panel. Default false: this
//                     component also renders INSIDE cards (the tab panels on
//                     SubscriberBeneficiaryDetailPage, PatientActivityTrail),
//                     where a second border would nest inside the first
//   hero            — larger, softer presentation for a page's main empty state
//                     (bigger icon tile, display-size title, CTA with an icon).
//                     `action.icon` / `hintIcon` take names from the shared Icon set
//   compact         — tighter padding, for empty states inside small summary
//                     cards rather than filling a whole page
import React from 'react';
import Icon, { ICON_NAMES } from './Icon';

export default function EmptyState({
  icon = 'inbox',
  title,
  message,
  action,
  secondaryAction,
  hint,
  framed = false,
  compact = false,
  hero = false,
  hintIcon,
}) {
  const isNamedIcon = ICON_NAMES.includes(icon);
  const className = `empty-state${framed && !compact ? ' empty-state--panel' : ''}${hero && !compact ? ' empty-state--hero' : ''}`;

  return (
    <div className={className} style={compact ? { padding: '20px 12px' } : undefined}>
      {isNamedIcon ? (
        <div
          className="empty-icon empty-icon--badge"
          aria-hidden="true"
          style={compact ? { width: 40, height: 40, marginBottom: 10 } : undefined}
        >
          <Icon name={icon} size={compact ? 16 : 20} />
        </div>
      ) : (
        // Emoji path — every pre-existing call site passes one of these, and
        // they keep the look they already have rather than being restyled by a
        // change they did not ask for.
        <div
          className="empty-icon"
          aria-hidden="true"
          style={compact ? { fontSize: 28, marginBottom: 8 } : undefined}
        >
          {icon}
        </div>
      )}

      {title && <div className="empty-title">{title}</div>}
      {message && <p className="empty-desc">{message}</p>}

      {(action || secondaryAction) && (
        <div className="empty-actions">
          {action && (
            <button type="button" className="btn btn-primary" onClick={action.onClick}>
              {action.icon && <Icon name={action.icon} size={16} />}
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button type="button" className="empty-link" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}

      {hint && (
        <div className="empty-hint">
          {hintIcon && <Icon name={hintIcon} size={14} />}
          {hint}
        </div>
      )}
    </div>
  );
}
